import { auth } from "../lib/firebase";
import {
  AuthCredential,
  ConfirmationResult,
  GoogleAuthProvider,
  OAuthProvider,
  RecaptchaVerifier,
  User,
  createUserWithEmailAndPassword,
  deleteUser,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
} from "firebase/auth";

import { endAllFormSessions } from "@/hooks/use-in-person-form-lock";

import {
  signalNativeSignOut,
  takeNativeCustomToken,
} from "@/lib/native-bridge";
import { toast } from "@/lib/use-toast";

import { signInWithVerifiedMicrosoftEmail } from "@/api/auth/login";

const RECAPTCHA_CONTAINER_ID = "firebase-recaptcha-container";

/**
 * Pull the human-readable reason out of a Firebase auth blocking-function rejection.
 *
 * The server reports `BLOCKING_FUNCTION_ERROR_RESPONSE : HTTP Cloud Function returned an
 * error: {"error":{"message":"..."}}`, but the SDK splits off that code before building
 * the error, so what reaches us is `auth/internal-error` with a message like
 * `Firebase: HTTP Cloud Function returned an error: {"error":{...}} (auth/internal-error).`
 * The emulator wraps the same JSON differently. Key on the code and the JSON payload,
 * never on the envelope text. The inner message is the only part worth showing — it
 * tells the user which Microsoft 365 tenant needs allowlisting.
 */
function readBlockingFunctionMessage(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code !== "auth/internal-error") return null;
  const raw = String(message ?? "");
  const jsonStart = raw.indexOf('{"error"');
  const jsonEnd = raw.lastIndexOf("}");
  if (jsonStart === -1 || jsonEnd <= jsonStart) return null;
  try {
    const parsed = JSON.parse(raw.slice(jsonStart, jsonEnd + 1));
    const reason = parsed?.error?.message;
    if (typeof reason === "string" && reason.trim()) return reason.trim();
  } catch {
    // Envelope shape changed; fall through to the generic message below.
  }
  return "Microsoft sign-in was refused for this account.";
}
function microsoftProvider(): OAuthProvider {
  const provider = new OAuthProvider("microsoft.com");
  // Without the email scope Microsoft leaves `email` out of the ID token, and with it the
  // `xms_edov` claim that verified-email sign-in depends on.
  provider.addScope("email");
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

/** The message for a Microsoft sign-in or connect failure common to both flows. */
function describeMicrosoftError(error: any, action: string): string {
  if (error?.code === "auth/operation-not-allowed") {
    return "Microsoft sign-in is not currently configured. Please reach out to the Decoda Engineering team to set it up for you.";
  }
  if (
    error?.code === "auth/popup-closed-by-user" ||
    error?.code === "auth/cancelled-popup-request"
  ) {
    return "Microsoft sign-in was cancelled.";
  }
  // The blocking function's message arrives here; it explains what an operator needs
  // to do (allowlist the tenant, use a work account), so surface it rather than a
  // generic failure.
  const blocked = readBlockingFunctionMessage(error);
  if (blocked) return blocked;
  console.error(`Error ${action}:`, error);
  return `An error occurred while ${action}`;
}

// Upper bound on how long a single getToken() call waits for the first
// Firebase auth callback. On timeout that one call returns null without
// touching global auth state — see getToken(). Prevents a hung promise if
// Firebase never emits, while never publishing a synthetic logged-out state.
const TOKEN_READY_TIMEOUT_MS = 5_000;

// How often an open tab checks whether it has gone idle past the timeout.
const IDLE_CHECK_INTERVAL_MS = 30_000;

/**
 * Normalize a user-entered phone number to E.164. Assumes US (+1) when no
 * country code is present — callers passing already-E.164 strings pass through.
 */
function toE164(phoneNumber: string): string {
  const trimmed = phoneNumber.trim();
  if (trimmed.startsWith("+")) return `+${trimmed.slice(1).replace(/\D/g, "")}`;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  throw new Error("Invalid phone number format");
}

export interface GoogleSignInResult {
  user: User;
  /** Null when Firebase hands back no reusable OAuth credential. */
  credential: AuthCredential | null;
}

export class AuthService {
  private static instance: AuthService;
  private currentUser: User | null = null;
  private initialized: boolean = false;
  private authStateListeners: ((user: User | null) => void)[] = [];
  private refreshTokenPromise: Promise<string | undefined> | null = null;
  // Inactivity tracking state
  private inactivityTimeoutMs: number | null = null;
  private lastActivityAtMs: number = Date.now();
  private tracking: boolean = false;
  private activityKey: string = "decoda:activity:lastActivityAt";
  private lastEmitAtMs: number = 0;
  private removeActivityListeners: (() => void) | null = null;
  private loggingOut: boolean = false;
  // Shared by every concurrent logout() call so a second caller awaits the real
  // sign-out instead of resolving early and navigating while it's still pending.
  private logoutPromise: Promise<void> | null = null;
  private lastLogoutToastAtMs: number = 0;
  // Phone auth (patient MFA) state. Held between signInWithPhone (sends SMS)
  // and confirmPhoneCode (submits the 6-digit code).
  private pendingPhoneConfirmation: ConfirmationResult | null = null;
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  // The session the mobile app handed this page (see `adoptNativeSession`),
  // until it has been exchanged. Read once, before any page code runs.
  private nativeToken: string | null = takeNativeCustomToken();
  // Auth initialization state
  private authReadyPromise: Promise<void>;
  private resolveAuthReady: () => void = () => {};
  private authInitialized: boolean = false;
  // The user last broadcast to listeners. Tracked separately from
  // `currentUser` because logout() clears that before Firebase emits the
  // sign-out, so `currentUser` cannot tell us what listeners already saw.
  private lastNotifiedUid: string | null = null;
  private hasNotified: boolean = false;

  private constructor() {
    this.authReadyPromise = new Promise((resolve) => {
      this.resolveAuthReady = resolve;
    });

    // whenReady() resolves when Firebase emits its initial auth state or a
    // credential operation returns a definitive signed-in user. We deliberately
    // do NOT add a timer fallback that publishes a synthetic logged-out state:
    // that would falsely log out users on slow initialization. Bounding the wait
    // is done per-call in getToken(), so a slow-but-eventual Firebase emit still
    // recovers.
    onAuthStateChanged(auth, (user) => {
      if (!user && this.nativeToken) {
        this.adoptNativeSession(this.nativeToken);
        return;
      }
      // Already signed in here (a WebView that kept its session): the app's
      // token isn't needed.
      this.nativeToken = null;
      const nextUid = user?.uid ?? null;
      this.currentUser = user;
      this.markAuthReady();
      // A credential promise may have already published this exact user before
      // Firebase's observer catches up. Skip that duplicate, comparing against
      // what was last broadcast — comparing against `currentUser` would
      // swallow the sign-out, since logout() nulls it before signOut() and the
      // observer's null would then look like no change at all.
      if (!this.hasNotified || this.lastNotifiedUid !== nextUid) {
        // A user arriving after the first emit signed in on this page (email
        // link, popup). The first emit itself is a session Firebase restored,
        // which keeps its old activity time so the timeout can still end it.
        if (nextUid && this.hasNotified) this.updateActivityTimestamp();
        this.notifyListeners(user);
      }
    });
  }

  /**
   * Sign in with the session the mobile app handed this page, before anyone
   * hears that auth is ready.
   *
   * The app embeds the console in a WebView with its own storage, so Firebase
   * first reports nobody. Published, that would send the page to the login
   * screen and its post-login redirect: a second full page load, each one a
   * blank flash and a spinner. Held back until the exchange is done, the page
   * the app opened loads once, signed in. A failed exchange publishes the
   * signed-out state as usual, and the page shows the login screen.
   *
   * A custom token rather than the app's ID token, because the session then
   * holds its own refresh token and lasts a whole shift.
   */
  private adoptNativeSession(token: string): void {
    this.nativeToken = null;
    signInWithCustomToken(auth, token).then(
      (result) => this.setAuthenticatedUser(result.user),
      (error) => {
        console.error("[native-bridge] custom token sign-in failed:", error);
        this.currentUser = null;
        this.markAuthReady();
        if (!this.hasNotified) this.notifyListeners(null);
      },
    );
  }

  private markAuthReady(): void {
    this.initialized = true;
    if (this.authInitialized) return;

    this.authInitialized = true;
    this.resolveAuthReady();
  }

  public isAuthInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Returns a Promise that resolves when Firebase auth state is first determined.
   * Use this to wait for auth initialization before making API calls.
   */
  public whenReady(): Promise<void> {
    return this.authReadyPromise;
  }

  private notifyListeners(user: User | null) {
    this.lastNotifiedUid = user?.uid ?? null;
    this.hasNotified = true;
    this.authStateListeners.forEach((listener) => listener(user));
  }

  /**
   * Publish a user returned directly by a Firebase sign-in operation.
   *
   * Firebase resolves sign-in promises before its auth-state observer is
   * guaranteed to run. Keeping the credential only in the caller creates a
   * short window where authenticated API requests see no current user. This is
   * especially visible after patient sign-up, which navigates immediately and
   * loads the protected portal before the observer catches up.
   */
  private setAuthenticatedUser(user: User): User {
    this.currentUser = user;
    // A fresh sign-in is activity. Without this, an idle timestamp left by the
    // previous user would sign this one straight back out.
    this.updateActivityTimestamp();
    this.markAuthReady();
    this.notifyListeners(user);
    return user;
  }

  public addAuthStateListener(listener: (user: User | null) => void) {
    this.authStateListeners.push(listener);
    if (this.initialized) {
      listener(this.currentUser);
    }
    return () => {
      this.authStateListeners = this.authStateListeners.filter(
        (l) => l !== listener,
      );
    };
  }

  public async refreshAuthToken(): Promise<string | undefined> {
    if (this.refreshTokenPromise) return this.refreshTokenPromise;

    this.refreshTokenPromise = (async () => {
      if (this.currentUser) {
        await this.currentUser.reload();
        const refreshedToken = await this.currentUser.getIdToken(true);

        return refreshedToken;
      }
      return undefined;
    })();

    // finally, not a bare await: clearing only on success leaves a rejected
    // promise cached, and the guard above then hands that same rejection to
    // every later caller. One network blip would poison token refresh for the
    // life of the page — including getToken, which routes through here when a
    // token has expired.
    try {
      return await this.refreshTokenPromise;
    } finally {
      this.refreshTokenPromise = null;
    }
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async login(email: string, password: string): Promise<User> {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    return this.setAuthenticatedUser(userCredential.user);
  }

  public async signInWithEmailLink(email: string, link: string): Promise<User> {
    const credential = await signInWithEmailLink(auth, email, link);
    return this.setAuthenticatedUser(credential.user);
  }

  /** Create a new Firebase account (used for patient portal sign-up). */
  public async createUser(email: string, password: string): Promise<User> {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    return this.setAuthenticatedUser(userCredential.user);
  }

  /**
   * Trigger Firebase Phone Auth to send a verification SMS. Accepts numbers
   * in any common US format — bare 10-digit, "(555) 123-4567", or already
   * E.164 — and normalizes to E.164 before calling Firebase. Throws on failure.
   */
  public async signInWithPhone(phoneNumber: string): Promise<void> {
    const verifier = this.getRecaptchaVerifier();
    try {
      this.pendingPhoneConfirmation = await signInWithPhoneNumber(
        auth,
        toE164(phoneNumber),
        verifier,
      );
    } finally {
      // reCAPTCHA tokens are single-use; always discard the verifier so the
      // next send (resend or retry after error) renders a fresh one.
      try {
        this.recaptchaVerifier?.clear();
      } catch {
        // ignore
      }
      this.recaptchaVerifier = null;
    }
  }

  /**
   * Confirm a Firebase Phone Auth code from a prior signInWithPhone call.
   * Returns the signed-in user; throws if no pending confirmation or invalid code.
   */
  public async confirmPhoneCode(code: string): Promise<User> {
    if (!this.pendingPhoneConfirmation) {
      throw new Error(
        "No pending phone verification. Call signInWithPhone first.",
      );
    }
    const credential = await this.pendingPhoneConfirmation.confirm(code);
    this.pendingPhoneConfirmation = null;
    return this.setAuthenticatedUser(credential.user);
  }

  private getRecaptchaVerifier(): RecaptchaVerifier {
    if (this.recaptchaVerifier) return this.recaptchaVerifier;
    // Always start from a clean container. The reCAPTCHA widget marks the
    // host element as "already rendered" even after the verifier is cleared,
    // and the container persists across pages because AuthService is a
    // module singleton — so the next `new RecaptchaVerifier` against the
    // stale container throws "reCAPTCHA has already been rendered".
    const existing = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (existing) existing.remove();
    const container = document.createElement("div");
    container.id = RECAPTCHA_CONTAINER_ID;
    container.style.display = "none";
    document.body.appendChild(container);
    this.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      RECAPTCHA_CONTAINER_ID,
      {
        size: "invisible",
      },
    );
    return this.recaptchaVerifier;
  }

  /**
   * Google popup sign-in that also hands back the reusable OAuth credential.
   *
   * The credential matters to flows that must step through a second Firebase
   * identity part-way (patient sign-up verifies a phone number, and that swaps
   * the session to the phone user). Holding it lets the flow re-enter the
   * Google session silently instead of opening a popup the user never asked
   * for a second time.
   */
  public async signInWithGooglePopup(): Promise<GoogleSignInResult | string> {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      return {
        user: this.setAuthenticatedUser(result.user),
        credential: GoogleAuthProvider.credentialFromResult(result),
      };
    } catch (error: any) {
      if (error.code === "auth/operation-not-allowed") {
        return "Google signin is not currently configured for your tenant. Please reach out to the Decoda Engineering team to set it up for you.";
      }
      console.error("Error signing in with Google:", error);
      return "An error occurred while signing in with Google";
    }
  }

  public async loginWithGoogle(): Promise<User | string> {
    const result = await this.signInWithGooglePopup();
    return typeof result === "string" ? result : result.user;
  }

  /**
   * Re-enter a Google session from a credential captured by an earlier popup.
   * Silent — no popup, no user gesture. Google ID tokens only stay valid for
   * about an hour, so callers must handle this throwing and fall back to
   * `signInWithGooglePopup`.
   */
  public async signInWithGoogleCredential(
    credential: AuthCredential,
  ): Promise<User> {
    const result = await signInWithCredential(auth, credential);
    return this.setAuthenticatedUser(result.user);
  }

  /**
   * Redeem a server-minted custom token. Patients who verified their phone
   * use this to continue as the portal account their Patient row is bound to.
   */
  public async signInWithCustomToken(token: string): Promise<User> {
    const result = await signInWithCustomToken(auth, token);
    return this.setAuthenticatedUser(result.user);
  }

  public async loginWithMicrosoft(): Promise<User | string> {
    // Entra's own consent screen is the only place a work/school account can be chosen,
    // so `prompt: select_account` keeps a shared machine from silently reusing whoever
    // signed in last. Which organizations may actually get in is enforced server-side by
    // the auth blocking function on the `tid` claim — never here, and never on the email.
    try {
      const result = await signInWithPopup(auth, microsoftProvider());
      return this.setAuthenticatedUser(result.user);
    } catch (error: any) {
      if (error.code === "auth/account-exists-with-different-credential") {
        // Linking an identity to an account the user has not proven they own would hand it
        // that account's uid, the whole authorization binding. The one exception is an email
        // Microsoft vouches for (its domain is verified in the signing organization), checked
        // server-side; anything else signs in the usual way and connects from settings.
        const joined = await this.joinWithVerifiedMicrosoftEmail(error);
        if (joined) return joined;
        return "An account already exists with this email address. Sign in the way you did before, then connect Microsoft from your profile settings.";
      }
      return describeMicrosoftError(error, "signing in with Microsoft");
    }
  }

  /**
   * Sign in to the existing account with this email when the API confirms Microsoft vouches
   * for it, then attach the Microsoft sign-in so the next one goes straight through. Anything
   * short of a full success signs back out and returns null, leaving the usual message.
   */
  private async joinWithVerifiedMicrosoftEmail(
    error: unknown,
  ): Promise<User | string | null> {
    try {
      const credential = OAuthProvider.credentialFromError(error as never);
      if (!credential?.idToken) return null;
      const result = await signInWithVerifiedMicrosoftEmail(credential.idToken);
      if (!result?.customToken) return null;
      const { user } = await signInWithCustomToken(auth, result.customToken);
      try {
        await linkWithCredential(user, credential);
      } catch (linkError: any) {
        if (linkError?.code !== "auth/provider-already-linked") {
          await signOut(auth);
          return null;
        }
      }
      return this.setAuthenticatedUser(user);
    } catch {
      if (auth.currentUser) await signOut(auth);
      return null;
    }
  }

  /**
   * Attach a Microsoft account to the signed-in user, so they can sign in with Microsoft
   * from then on. Safe where login-time linking is not: the user has already proven they
   * own this account by signing in, so no email is trusted. The auth blocking function
   * still checks the Entra tenant allowlist when the link is made.
   */
  public async connectMicrosoft(): Promise<User | string> {
    const user = auth.currentUser;
    if (!user)
      return "Your session has expired. Sign in again to connect Microsoft.";
    try {
      const result = await linkWithPopup(user, microsoftProvider());
      return this.setAuthenticatedUser(result.user);
    } catch (error: any) {
      if (error.code === "auth/provider-already-linked") {
        return "Microsoft is already connected to your account.";
      }
      if (
        error.code === "auth/credential-already-in-use" ||
        error.code === "auth/email-already-in-use"
      ) {
        return "This Microsoft account is already connected to a different Decoda account.";
      }
      if (error.code === "auth/requires-recent-login") {
        return "For your security, sign out and sign back in, then connect Microsoft.";
      }
      return describeMicrosoftError(error, "connecting Microsoft");
    }
  }

  public async deleteCurrentUser(): Promise<void> {
    if (this.currentUser) {
      await deleteUser(this.currentUser);
      await signOut(auth);
    }
  }

  public logout(suppressToast: boolean = false): Promise<void> {
    if (this.logoutPromise) return this.logoutPromise;
    this.logoutPromise = this.performLogout(suppressToast).finally(() => {
      this.logoutPromise = null;
    });
    return this.logoutPromise;
  }

  private async performLogout(suppressToast: boolean): Promise<void> {
    this.loggingOut = true;
    const now = Date.now();
    const timedOut =
      this.inactivityTimeoutMs != null &&
      now - this.lastActivityAtMs >= this.inactivityTimeoutMs;

    // Show a toast informing the user they were logged out (unless suppressed)
    if (!suppressToast) {
      try {
        if (now - this.lastLogoutToastAtMs > 3000) {
          toast({
            variant: timedOut ? "warning" : "info",
            title: "Signed out",
            description: timedOut
              ? "You were signed out due to inactivity."
              : "You have been signed out.",
          });
          this.lastLogoutToastAtMs = now;
        }
      } catch {
        console.error("Error showing toast");
      }
    }

    this.currentUser = null;
    this.stopActivityTracking();
    try {
      await signOut(auth);
      // The next sign-in starts its own clock; nothing of this session's
      // idle time should carry over to it.
      try {
        window.localStorage.removeItem(this.activityKey);
      } catch {
        console.error("Error clearing activity timestamp");
      }
      // Inside the mobile app, a sign-out the user chose ends the app's own
      // session as well. Not an inactivity timeout: the app's session is what
      // lets the phone ring for calls, and an idle console shouldn't stop that.
      if (!timedOut) signalNativeSignOut();
      // Only after the session is actually gone: if signOut rejected we stay
      // authenticated, so the in-person lock must remain to keep tabs gated.
      // If the clear itself couldn't be persisted, surface it — a stale lock
      // left in storage would gate the console on the next sign-in until it is
      // cleared, so it must not pass silently.
      if (!endAllFormSessions()) {
        console.error(
          "Sign-out could not clear the in-person form lock; a stale lock may remain in storage.",
        );
      }
    } finally {
      this.loggingOut = false;
    }
  }

  public async handleIPBlockingError(error: any): Promise<void> {
    // Check if this is an IP blocking error (403 Forbidden)
    if (error?.response?.status === 403) {
      let errorMessage =
        "Your IP address is not in the allowed list. Please request access or contact your administrator.";

      // Try to extract the error message from the response
      if (error.response) {
        try {
          // ky errors have response.json() method to parse the response body
          const errorData = await error.response.json();
          if (errorData?.detail) {
            errorMessage = errorData.detail;
          } else if (typeof errorData === "string") {
            errorMessage = errorData;
          }
        } catch {
          // If JSON parsing fails, use default message
        }
      }

      // Remove "Access denied." prefix if present (already in title)
      const cleanedMessage = errorMessage.replace(/^Access denied\.\s*/i, "");

      toast({
        title: "Access Denied",
        description: cleanedMessage,
        variant: "error",
      });
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }

  public async getToken(forceRefresh = false): Promise<string | null> {
    // Patient phone-auth and provider sign-in both ride the Firebase ID
    // token now — no separate MFA token to juggle.
    // Wait for the first Firebase auth callback, but bound the wait so a
    // never-arriving callback can't hang this promise forever. On timeout we
    // fall through and return null for THIS call only — global auth state is
    // left untouched, so a later Firebase emit still authenticates normally.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      this.whenReady(),
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, TOKEN_READY_TIMEOUT_MS);
      }),
    ]);
    if (timeoutId) clearTimeout(timeoutId);

    if (!this.currentUser) {
      return null;
    }

    const currentToken = await this.currentUser.getIdToken(false);
    if (!currentToken || forceRefresh || this.isTokenExpired(currentToken)) {
      const refreshedToken = await this.refreshAuthToken();
      return refreshedToken ?? null;
    }

    return currentToken;
  }

  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Legacy MFA token API — retained as no-ops so existing call sites compile.
  // Firebase ID tokens are now the only patient-auth token; the SDK handles
  // refresh and expiry, so these don't need to do anything.
  public async setMfaToken(_token: string): Promise<void> {}
  public async getMfaToken(): Promise<string | null> {
    return this.getToken();
  }
  public getRawMfaToken(): string | null {
    return null;
  }
  public clearMfaToken(): void {}

  // ===========================
  // Inactivity tracking API
  // ===========================

  public setInactivityTimeout(milliseconds: number | null): void {
    // A clinic without a timeout still records activity, it just never acts
    // on it: a user who works there and then opens a clinic with a timeout,
    // in this tab or another, must not be signed out over that time.
    this.inactivityTimeoutMs = milliseconds;
    this.startActivityTracking();
  }

  public isIdleBeyondTimeout(): boolean {
    if (this.inactivityTimeoutMs == null) return false;
    return Date.now() - this.lastActivityAtMs >= this.inactivityTimeoutMs;
  }

  public getLastActivityAt(): number {
    return this.lastActivityAtMs;
  }

  public get isLoggingOut(): boolean {
    return this.loggingOut;
  }

  private startActivityTracking(): void {
    if (this.tracking || typeof window === "undefined") return;
    this.tracking = true;

    const fromStorage = this.readStoredActivity();
    if (fromStorage != null) {
      this.lastActivityAtMs = fromStorage;
      // A page opened after the timeout has passed (a restored tab, a reload,
      // a reopened browser) is still the same idle session: Firebase kept the
      // login, so the timeout is the only thing that can end it.
      if (this.isIdleBeyondTimeout()) {
        if (this.currentUser) {
          void this.logout();
          return;
        }
        this.updateActivityTimestamp();
      }
    } else {
      this.updateActivityTimestamp();
    }

    const onUserAction = () => this.handleInteractionEvent();
    const onPassiveActivity = () => this.handleInteractionEvent();

    const onStorage = (e: StorageEvent) => {
      if (e.key === this.activityKey && typeof e.newValue === "string") {
        const ts = Number(e.newValue);
        if (Number.isFinite(ts) && ts > this.lastActivityAtMs)
          this.lastActivityAtMs = ts;
      }
    };

    window.addEventListener("keydown", onUserAction, true);
    window.addEventListener("pointerdown", onUserAction, true);
    window.addEventListener("touchstart", onUserAction, true);
    window.addEventListener("scroll", onPassiveActivity, {
      passive: true,
    } as AddEventListenerOptions);
    window.addEventListener("storage", onStorage);
    // Interaction alone can't end a session nobody touches: a tab left open on
    // a chart would stay signed in, and connected, until the next click.
    const idleCheck = window.setInterval(() => {
      if (!this.loggingOut && this.isIdleAcrossTabs()) void this.logout();
    }, IDLE_CHECK_INTERVAL_MS);

    this.removeActivityListeners = () => {
      window.clearInterval(idleCheck);
      window.removeEventListener("keydown", onUserAction, true);
      window.removeEventListener("pointerdown", onUserAction, true);
      window.removeEventListener("touchstart", onUserAction, true);
      window.removeEventListener("scroll", onPassiveActivity as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }

  private stopActivityTracking(): void {
    if (!this.tracking) return;
    this.tracking = false;
    if (this.removeActivityListeners) {
      this.removeActivityListeners();
      this.removeActivityListeners = null;
    }
  }

  private readStoredActivity(): number | null {
    try {
      const ts = Number(window.localStorage.getItem(this.activityKey));
      return Number.isFinite(ts) && ts > 0 ? ts : null;
    } catch {
      console.error("Error reading activity timestamp");
      return null;
    }
  }

  // A backgrounded tab can run an overdue check before the storage events
  // from the tab the user is working in arrive, so before acting on its own
  // stale value it reads the shared one directly.
  private isIdleAcrossTabs(): boolean {
    if (!this.isIdleBeyondTimeout()) return false;
    const ts = this.readStoredActivity();
    if (ts != null && ts > this.lastActivityAtMs) this.lastActivityAtMs = ts;
    return this.isIdleBeyondTimeout();
  }

  private updateActivityTimestamp(now: number = Date.now()): void {
    this.lastActivityAtMs = now;
    this.lastEmitAtMs = now;
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(this.activityKey, String(now));
    } catch {
      console.error("Error updating activity timestamp");
    }
  }

  private handleInteractionEvent(): void {
    const now = Date.now();
    if (this.loggingOut) return;
    if (this.isIdleAcrossTabs()) {
      void this.logout();
      return;
    }
    if (now - this.lastEmitAtMs < 10_000) return;
    this.updateActivityTimestamp(now);
  }
}

export const authService = AuthService.getInstance();
